<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, SliderV2 } from '@Shelly/shelly-design-system';
	import CameraStreamPreview from './CameraStreamPreview.svelte';
	import { getCameraConfig, saveCameraConfig } from '../../helpers/camera-settings-helpers';
	import { hasCameraConfigKey } from '../../helpers/camera-features';
	import type {
		CameraImageAdjustmentSliderKey,
		CameraImageAdjustmentsState,
	} from '../../types/camera-settings';

	export let device: Device;

	type VideoSlider = {
		key: CameraImageAdjustmentSliderKey;
		title: string;
		min: number;
		max: number;
		defaultValue: number;
		lineMarkers: { value: number; showLabel: boolean }[];
		supported: boolean;
	};

	const DEFAULT_MARKERS = [
		{ value: 0, showLabel: true },
		{ value: 50, showLabel: true },
		{ value: 100, showLabel: true },
	];

	const TINT_MARKERS = [
		{
			value: -256,
			displayValue: $format('camera_video_tint_magenta', { default: 'Magenta' }),
			showLabel: true,
		},
		{ value: -192, showLabel: false },
		{ value: -128, showLabel: false },
		{ value: -64, showLabel: false },
		{ value: 0, showLabel: true },
		{ value: 64, showLabel: false },
		{ value: 128, showLabel: false },
		{ value: 192, showLabel: false },
		{
			value: 256,
			displayValue: $format('camera_video_tint_green', { default: 'Green' }),
			showLabel: true,
		},
	];

	const TEMPERATURE_MARKERS = [
		{
			value: -256,
			displayValue: $format('camera_video_temperature_cold', { default: 'Cold' }),
			showLabel: true,
		},
		{ value: -192, showLabel: false },
		{ value: -128, showLabel: false },
		{ value: -64, showLabel: false },
		{
			value: 0,
			displayValue: $format('camera_video_temperature_neutral', { default: 'Neutral' }),
			showLabel: true,
		},
		{ value: 64, showLabel: false },
		{ value: 128, showLabel: false },
		{ value: 192, showLabel: false },
		{
			value: 256,
			displayValue: $format('camera_video_temperature_warm', { default: 'Warm' }),
			showLabel: true,
		},
	];

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const VIDEO_SLIDERS: VideoSlider[] = (
		[
			{
				key: 'brightness',
				title: $format('camera_video_brightness', { default: 'Brightness' }),
				min: 0,
				max: 100,
				defaultValue: 50,
				lineMarkers: DEFAULT_MARKERS,
				supported: true,
			},
			{
				key: 'contrast',
				title: $format('camera_video_contrast', { default: 'Contrast' }),
				min: 0,
				max: 100,
				defaultValue: 50,
				lineMarkers: DEFAULT_MARKERS,
				supported: true,
			},
			{
				key: 'saturation',
				title: $format('camera_video_saturation', { default: 'Saturation' }),
				min: 0,
				max: 100,
				defaultValue: 50,
				lineMarkers: DEFAULT_MARKERS,
				supported: true,
			},
			{
				key: 'sharpness',
				title: $format('camera_video_sharpness', { default: 'Sharpness' }),
				min: 0,
				max: 100,
				defaultValue: 50,
				lineMarkers: DEFAULT_MARKERS,
				supported: true,
			},
			{
				key: 'tint',
				title: $format('camera_video_tint', { default: 'Tint' }),
				min: -256,
				max: 256,
				defaultValue: 0,
				lineMarkers: TINT_MARKERS,
				supported: hasCameraConfigKey(device, 'video.tint'),
			},
			{
				key: 'temperature',
				title: $format('camera_video_temperature', { default: 'Color temperature' }),
				min: -256,
				max: 256,
				defaultValue: 0,
				lineMarkers: TEMPERATURE_MARKERS,
				supported: hasCameraConfigKey(device, 'video.temperature'),
			},
		] as VideoSlider[]
	).filter((slider) => slider.supported);

	const readVideo = (): CameraImageAdjustmentsState => {
		const raw = getCameraConfig(device)?.video;
		const state = {} as CameraImageAdjustmentsState;
		for (const { key, defaultValue } of VIDEO_SLIDERS) {
			state[key] = typeof raw?.[key] === 'number' ? raw[key] : defaultValue;
		}
		return state;
	};

	let video: CameraImageAdjustmentsState = readVideo();
	let cache: CameraImageAdjustmentsState = { ...video };
	let loading = false;

	const AUTOSAVE_DELAY = 500;
	let saveTimeout: ReturnType<typeof setTimeout> | undefined;

	$: hasChanges = VIDEO_SLIDERS.some(({ key }) => video[key] !== cache[key]);

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: resetToDefaults,
				loading,
				disabled: loading,
				label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const scheduleSave = () => {
		if (saveTimeout) clearTimeout(saveTimeout);
		saveTimeout = setTimeout(save, AUTOSAVE_DELAY);
	};

	const save = async () => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = undefined;
		}
		if (!hasChanges) return;
		loading = true;
		const currentVideo = getCameraConfig(device)?.video ?? {};
		const success = await saveCameraConfig(
			device,
			{ video: { ...currentVideo, ...video } },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cache = { ...video };
		} else {
			video = { ...cache };
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		loading = true;
		const currentVideo = getCameraConfig(device)?.video ?? {};
		const resetVideo = { ...currentVideo };
		for (const { key } of VIDEO_SLIDERS) {
			resetVideo[key] = null;
		}
		const success = await saveCameraConfig(
			device,
			{ video: resetVideo },
			$format('camera_video_reset_success', { default: 'Video settings reset to defaults.' }),
		);
		if (success) {
			video = readVideo();
			cache = { ...video };
		}
		loading = false;
	};

	onDestroy(() => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			save();
		}
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<CameraStreamPreview {device} />
{/if}

{#each VIDEO_SLIDERS as { key, title, min, max, lineMarkers } (key)}
	<SliderV2
		id="camera-video-{key}"
		class="mb-s-plus"
		value={video[key]}
		on:value_change={(e) => {
			video = { ...video, [key]: e.detail.value };
			scheduleSave();
		}}
		disabled={loading}
		{min}
		{max}
		step={1}
		type="incremented"
		{title}
		titleTypography="body-small"
		unit=""
		{lineMarkers}
		a11yProps={{
			ariaLabel: title,
			ariaLabelValue: `${title} ${$format('label_value', { default: 'value' })}`,
		}}
	/>
{/each}

{#if !$showNewLayout}
	<Button
		variant="secondary"
		width="full"
		size="m"
		{loading}
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
{/if}
